import React from 'react';
import { connect } from 'react-redux';
import { defineMessages, injectIntl } from 'react-intl';
import { Link } from 'react-router';
import classnames from 'classnames';
import moment from 'moment';

import SelectLanguageButton from './SelectLanguageButton';
import ThemeToggle from './ThemeToggle';
import { trunc } from '../helpers/index';
import { toggleOrderSidebar } from '../actions/company';

const messages = defineMessages({
  PROPERTIES: {
    id: 'Breadcrumb.PROPERTIES',
    defaultMessage: 'Einstellungen',
  },
  LOG_OUT: {
    id: 'Breadcrumb.LOG_OUT',
    defaultMessage: 'Ausloggen',
  },
  EXPIRATION_DATE: {
    id: 'Breadcrumb.EXPIRATION_DATE',
    defaultMessage: 'Ihr Test-Nutzerkonto läuft {expirationDate} ab.',
  },
  BACK_TO_OVERVIEW: {
    id: 'NavBar.BACK_TO_OVERVIEW',
    defaultMessage: 'Zurück zur Übersicht',
  },
});

function NavBarComponent({ config, toggleSidebar, toggleOrderSidebar, intl: { formatMessage } }) {
  const { user, company, companyMembership, currentRouteName = '', hideSidebar } = config;

  if (!user) {
    return null;
  }

  let expirationDate;
  let showTestAccountNotice = false;
  const isGlobalAdmin = user && user.is_admin;
  const isCompanyAdmin = companyMembership && (companyMembership.is_owner || companyMembership.is_admin);
  const isAdmin = isGlobalAdmin || isCompanyAdmin;

  if (company &&
    isAdmin &&
    !company.hasPaymentData &&
    currentRouteName.indexOf('company-payment') === -1) {
    const trialDays = company.trial_days || 14;
    expirationDate = moment(company.created).add(trialDays, 'days');
    showTestAccountNotice = true;
  }

  return (
    <div style={{ boxSizing: 'border-box', height: 'auto', minHeight: '56px', zIndex: 1050, position: 'relative', backgroundColor: 'var(--nav-bg)', borderBottom: '1px solid var(--panel-border)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '55px', padding: '0 24px' }}>
        
        {/* Left Side: Home / Back Button & Language */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <style>{`
            @media (max-width: ${hideSidebar ? '1100px' : '992px'}) {
              .mobile-hamburger { display: block !important; }
            }
          `}</style>
          {(company || user) && (
            <Link 
              to={company ? "/company/" : "/"} 
              className="lh-btn lh-btn-small lh-btn-default" 
              style={{ 
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                height: '34px', boxSizing: 'border-box',
                textDecoration: 'none', fontWeight: '500',
                fontSize: '13px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}
              title={formatMessage(messages.BACK_TO_OVERVIEW)}
            >
              <i className="fa fa-home" style={{ fontSize: '16px' }} />
              <span className="hidden-xs">{formatMessage(messages.BACK_TO_OVERVIEW)}</span>
            </Link>
          )}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <SelectLanguageButton />
          </div>
        </div>

        {/* Right Side: Theme, User Profile & Mobile Hamburger */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <ThemeToggle />
          </div>
          
          {/* User Profile Block */}
          <div className="hidden-xs" style={{ display: 'flex', alignItems: 'center', marginLeft: '16px', paddingLeft: '16px', borderLeft: '1px solid var(--panel-border)', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '6px 16px', borderRadius: '8px', backgroundColor: 'var(--hover-bg, rgba(0,0,0,0.02))' }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: 'var(--primary-color, #3b82f6)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '13px', flexShrink: 0 }}>
                {(user.firstname && user.lastname) ? `${user.firstname[0]}${user.lastname[0]}` : <i aria-hidden="true" className="fa fa-user" />}
              </div>
              <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-color)', whiteSpace: 'nowrap' }}>
                {user.email}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', marginLeft: '16px', paddingLeft: '16px', borderLeft: '1px solid var(--panel-border)', gap: '16px' }}>
            <Link to="/settings/" title={formatMessage(messages.PROPERTIES)} style={{ color: 'var(--text-muted)', textDecoration: 'none', transition: 'color 0.2s', display: 'flex', alignItems: 'center' }} onMouseEnter={(e) => e.currentTarget.style.color = 'var(--primary-color, #3b82f6)'} onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-muted)'}>
              <i className="fa fa-cog" style={{ fontSize: '18px' }} />
            </Link>

            <Link to="/logout/" title={formatMessage(messages.LOG_OUT)} style={{ color: 'var(--text-muted)', textDecoration: 'none', transition: 'color 0.2s', display: 'flex', alignItems: 'center' }} onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'} onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-muted)'}>
              <i className="fa fa-sign-out" style={{ fontSize: '18px' }} />
            </Link>
          </div>

          {/* Mobile Hamburger Menu (Right Side) */}
          {(isAdmin || hideSidebar) && (
            <button 
              className="mobile-hamburger"
              onClick={hideSidebar ? toggleOrderSidebar : toggleSidebar}
              style={{
                display: 'none', // Hidden by default, shown via CSS media query
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '8px', color: 'var(--text-color)', fontSize: '20px',
                marginLeft: '16px'
              }}
            >
              <i className="fa fa-bars" />
            </button>
          )}
        </div>
      </div>
      
      {showTestAccountNotice ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: '10px',
          padding: '10px', 
          background: 'linear-gradient(135deg, #0284c7, #2563eb, #4f46e5)', 
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          color: 'white',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          <i className="fa fa-info-circle" style={{ fontSize: '16px' }} />
          <Link
            style={{ color: 'white', textDecoration: 'none', borderBottom: '1px solid rgba(255,255,255,0.4)', transition: 'all 0.2s' }}
            to={`/company/${company.id}/payment`}
            onMouseEnter={(e) => e.currentTarget.style.borderBottom = '1px solid white'}
            onMouseLeave={(e) => e.currentTarget.style.borderBottom = '1px solid rgba(255,255,255,0.4)'}
          >
            {formatMessage(messages.EXPIRATION_DATE, { expirationDate: expirationDate.fromNow() })}
          </Link>
        </div>
      ) : null}
    </div>
  );
}

export default connect(
  state => ({ config: state.navbar }),
  dispatch => ({
    toggleOrderSidebar: () => dispatch(toggleOrderSidebar())
  })
)(injectIntl(NavBarComponent));
